<template>
    <Shell
        @close="selectedRoom && selectedRoom.id ? startClosing() : null"
        :show-close="!!(selectedRoom && selectedRoom.id)"
    >
        <template #header>
            <div class="flex items-center pb-3">
                <button
                    v-show="canGoBackToHome"
                    class="p-2 rounded-lg hover:bg-black-20%"
                    :title="__('Voltar')"
                    @click="voltar()"
                >
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <img :src="avatarUrl" class="ml-4 w-10 h-10 rounded-full" />

                <div class="ml-2">
                    <div class="font-bold">{{ headerTitle }}</div>
                    <div class="text-sm">{{ headerSubtitle }}</div>
                </div>
            </div>
        </template>

        <div class="overflow-hidden bg-gray-100 flex-grow flex flex-col">
            <div v-if="openingNewRoom">
                <div class="bg-white m-4 p-4 rounded flex items-center justify-center space-x-3">
                    <alo-loading-ring />
                    <div class="text-gray-600">{{ __('Iniciando atendimento...') }}</div>
                </div>
            </div>

            <div v-else-if="!eventos || eventos.length == 0" class="h-full flex flex-col justify-end">
                <div class="pt-10 pl-3 pb-2">
                    <div class="richt-text text-sm" v-html="firstMessageIntroduction.html"></div>

                    <div v-for="(option, index) in firstMessageOptions" :key="index">
                        <button
                            class="
                                mt-2
                                bg-white
                                py-1
                                px-2.5
                                rounded-lg
                                shadow-sm
                                text-gray-700
                                hover:text-black hover:shadow
                            "
                            @click="sendCannedFirstMessage(option.text)"
                        >
                            {{ option.text }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-else ref="mensagens" class="flex-grow px-2 overflow-auto" @scroll="onScroll($event)">
                <div ref="loadPreviousEvents" v-show="shouldDisplayLoadPreviousEvents">
                    <button @click="loadPreviousEvents()" class="mt-5 mb-5 bg-indigo-500 text-white">
                        {{ __('Carregar mais mensagens') }}
                    </button>
                </div>

                <div v-for="evento in contextualizedEvents" :key="evento.id">
                    <EventosShow
                        :sala="selectedRoom"
                        :evento="evento"
                        :show-avatar="true"
                        :perfil="perfil"
                        :user-profile="userProfile"
                        :contact-profile="contactProfile"
                        @select-option="handleSelectOption($event)"
                    />
                </div>

                <div v-show="showTypingIndicator" class="pl-2 py-2 flex items-center space-x-2">
                    <alo-avatar :src="typingIndicatorAvatar" class="w-6 h-6 rounded-sm" />
                    <div class="pl-6"><alo-dots-flashing /></div>
                </div>

                <!-- Aplicar padding-bottom no elemento de mensagens não está funcionando, por isso essa div. -->
                <div class="h-2"></div>
            </div>
        </div>

        <div v-if="showClosingDetails && !isClosed" class="relative p-4 bg-white">
            <button
                @click="showClosingDetails = false"
                class="
                    absolute
                    top-0
                    right-0
                    m-1
                    p-1
                    text-sm text-gray-600
                    rounded
                    hover:bg-gray-100 hover:text-gray-800
                "
                :title="__('Cancelar e voltar ao atendimento')"
            >
                <alo-icon icon="outline-x" class="w-5 h-5" />
            </button>

            <div v-show="isClosing" class="flex items-center space-x-2 justify-center p-unit">
                <alo-loading-ring class="w-4 h-4" />
                <span>{{ __('Encerrando') }}</span>
            </div>

            <div v-show="!isClosing">
                <div class="text-center text-gray-700">{{ __('Avalie o atendimento') }}</div>

                <div class="my-2 flex items-center justify-center space-x-2">
                    <button
                        v-for="index in [1, 2, 3, 4, 5]"
                        :key="index"
                        class="transform hover:scale-110"
                        @click="closeWithRating(index)"
                    >
                        <img :src="asset('/img/web_messenger/rating_' + index + '.png')" class="w-10 h-10" />
                    </button>
                </div>

                <div class="flex justify-center">
                    <button class="text-sm text-gray-700 underline" @click="closeWithoutRating()">
                        {{ __('Encerrar sem avaliar...') }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="isClosed" class="p-4 text-center bg-white">
            <div class="text-sm text-gray-700 flex items-center justify-center space-x-1">
                <alo-icon icon="outline-check" class="w-4 h-4" />
                <span>{{ __('Esse atendimento foi encerrado.') }}</span>
            </div>

            <div v-if="shouldShowRating">
                <div class="my-2 flex items-center justify-center space-x-2">
                    <button
                        v-for="index in [1, 2, 3, 4, 5]"
                        :key="index"
                        class="transform"
                        @click="
                            () => {
                                !selectedRoom.closing_rating && rateAfterClosing(index)
                            }
                        "
                    >
                        <img
                            :src="asset('/img/web_messenger/rating_' + index + '.png')"
                            class="w-10 h-10"
                            :class="{
                                'cursor-default': selectedRoom.closing_rating,
                                'opacity-50': selectedRoom.closing_rating && selectedRoom.closing_rating != index,
                            }"
                            :style="
                                selectedRoom.closing_rating && selectedRoom.closing_rating != index
                                    ? 'filter: grayscale(100%)'
                                    : ''
                            "
                        />
                    </button>
                </div>
            </div>

            <div class="flex justify-center" v-show="canGoBackToHomeAfterClose">
                <button class="text-gray-600 underline text-sm" @click="voltar()">
                    {{ __('Voltar') }}
                </button>
            </div>
        </div>

        <div v-if="selectedRoom && selectedRoom.status == 'queued' && !showClosingDetails" class="bg-white p-8">
            <div class="flex items-center justify-center space-x-2 text-gray-800">
                <alo-loading-ring />
                <span v-html="__('Você é a <b>:n</b> pessoa da fila.', { n: formattedQueuePosition })"></span>
            </div>

            <div class="mt-1 flex justify-center text-gray-700 text-sm">
                {{ __('Por favor, aguarde para ser atendido(a).') }}
            </div>
        </div>

        <div v-if="canSendMessage && !showClosingDetails">
            <EventosCreate
                class="pt-1.5"
                mensageiro-type="chat_salas"
                :mensageiro-id="selectedRoom && selectedRoom.id"
                canal="web_messenger"
                :attrs="eventosCreateAttrs"
                :can-send-audio="canSendAudio"
                :can-send-attachment="canSendAttachment"
                :show-close="showCloseOnBottomPanel"
                @close="startClosing()"
            />
        </div>
    </Shell>
</template>

<script>
import Shell from './Shell.vue'
import EventosShow from './Eventos/Show.vue'
import EventosCreate from '@App/Vue/Remote/Chat/Eventos/Create'
import PrimaryButton from './PrimaryButton'
import { perfil, userProfile, contactProfile } from '../Store/Store'
import { fastlane } from '../Store/Socket'
import { sleep } from '@Shared/Sleep'
import defaultAppearance from '../Store/AparênciaPadrão'
import '@App/Vendor/Trix'
import { asset } from '@App/GlobalHelpers'

export default {
    components: { EventosShow, EventosCreate, PrimaryButton, Shell },
    data() {
        return {
            autoScroll: true,
            perfil,
            userProfile,
            contactProfile: () => {
                return { name: __('Eu'), url_avatar_32px: asset('/img/avatar_default_user.png') }
            },
            mensagem: '',
            showingEmojiPicker: false,

            showClosingDetails: false,
            isClosing: false,

            eventosCreateAttrs: {
                env: 'widget',
                fastlane: fastlane,
                canaisDeBroadcast: this.$store.getters.canaisDeBroadcast,
                talvezMarcarEventosComoLidos: () => {
                    this.$store.dispatch('talvezMarcarEventosDaSalaSelecionadaComoLidos')
                },
                send: (data) => {
                    console.log(data)
                    this.$store.dispatch('abrirAtendimentoOuEnviarMensagemParaSalaSelecionada', data)
                },
            },
        }
    },
    async mounted() {
        this.scrollToBottomHandler = (ev) => {
            if (this.$store.getters.salaSelecionada && ev.detail.id_sala == this.$store.getters.salaSelecionada.id) {
                this.scrollToBottom(ev.detail.force)
            }
        }

        if (this.$refs.loadPreviousEvents) {
            new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.intersectionRatio >= 0.9) {
                            this.loadPreviousEvents()
                        }
                    })
                },
                {
                    threshold: 1.0,
                }
            ).observe(this.$refs.loadPreviousEvents)
        }

        document.addEventListener('chat:scroll-to-bottom', this.scrollToBottomHandler)

        let mensagem = this.$el.querySelector('#trix-mensagem')
        if (mensagem) mensagem.focus()
    },
    destroyed() {
        document.removeEventListener('chat:scroll-to-bottom', this.scrollToBottomHandler)
    },
    watch: {
        showTypingIndicator(value) {
            if (value) {
                this.scrollToBottom(false)
            }
        },
    },
    computed: {
        eventos() {
            if (this.selectedRoom) {
                return this.$store.getters.eventosDaSala(this.selectedRoom.id)
            }

            return []
        },

        avatarUrl() {
            if (this.idUser) {
                let url = userProfile(this.idUser).url_avatar_128px
                if (url) return url
            }

            return this.$store.state.aparência.chatDefaultAvatar
        },

        /**
         * O contato pode enviar áudio pelo widget?
         *
         * @return {boolean}
         */
        canSendAudio() {
            return this.$store.state.aparência.microphoneEnabled
        },

        /**
         * O contato pode enviar anexos no widget?
         *
         * @return {boolean}
         */
        canSendAttachment() {
            return this.$store.state.aparência.attachmentsEnabled
        },

        /**
         * Nós demovemos mostrar opção de encerrar no painel inferior?
         *
         * @return {boolean}
         */
        showCloseOnBottomPanel() {
            return this.$store.state.aparência.showCloseOnBottomPanel
        },

        /**
         * A avaliaçao deve ser mostrada se houve pelo menos uma mensagem enviada pelo operador.
         *
         * @return {boolean}
         */
        shouldShowRating() {
            let peloMenosUmEventoOperador = this.eventos.find((e) => e.autor_type == 'core_users')
            return !!peloMenosUmEventoOperador
        },

        /**
         * O contato pode voltar para a home depois que o atendimento foi encerrado?
         *
         * @return {boolean}
         */
        canGoBackToHomeAfterClose() {
            return !this.$store.state.aparência.showThankYouMessageAfterClose
        },

        /**
         * O usuário pode voltar para a home?
         *
         * @return {boolean}
         */
        canGoBackToHome() {
            return this.$store.state.maxAmountOfActiveRooms > 1 || this.$store.getters.activeRooms.length == 0
        },

        openingNewRoom() {
            return this.$store.state.openingNewRoom
        },

        headerTitle() {
            if (this.idUser) {
                return userProfile(this.idUser).name || __('Carregando...')
            }

            return this.$store.state.aparência.chatDefaultHeaderTitle
        },

        firstMessageIntroduction() {
            return this.$store.state.aparência.firstMessageIntroduction || defaultAppearance.firstMessageIntroduction
        },

        firstMessageOptions() {
            return this.$store.state.aparência.firstMessageOptions || defaultAppearance.firstMessageOptions
        },

        headerSubtitle() {
            if (this.$store.state.salaSelecionada) {
                let departament = this.$store.getters.findDepartamentById(
                    this.$store.state.salaSelecionada.department_id
                )
                if (departament) {
                    return departament.nome
                }
            }
        },

        idUser() {
            let room = this.selectedRoom
            if (!room) return null
            if (room.status != 'open') return null
            if (!room.atributos_extras) return null
            if (!room.atributos_extras.users_ids) return null
            return room.atributos_extras.users_ids[0]
        },

        /**
         * Sala que está selecionada atualmente.
         */
        selectedRoom() {
            return this.$store.getters.salaSelecionada
        },

        /**
         * Devemos mostrar indicação de "digitando..."?
         *
         * @return {boolean}
         */
        showTypingIndicator() {
            let room = this.selectedRoom
            if (!room) return false
            let key = 'chat_salas:' + room.id
            return this.$store.state.flashTyping[key]
        },

        typingIndicatorAvatar() {
            let room = this.selectedRoom
            if (!room) return false
            let key = 'chat_salas:' + room.id
            let data = this.$store.state.flashTyping[key]
            if (data) {
                let userId = data.autor.split(':').pop()
                return userProfile(userId).url_avatar_32px
            }
        },

        /**
         * Posição na fila atualmente.
         *
         * @return string
         */
        formattedQueuePosition() {
            if (!this.selectedRoom) return
            return this.$store.getters.queuePosition(this.selectedRoom.id)
        },

        /**
         * Nós devemos mostrar a opção de carregar mensagens mais antigas?
         *
         * @return {boolean}
         */
        shouldDisplayLoadPreviousEvents() {
            return (
                this.eventos.length >= this.$store.state.quantidadeDeEventosEmMemóriaPorSala &&
                !this.eventos[0].end_of_history
            )
        },

        /**
         * O contato pode enviar mensagens se o atendimento não tiver sido encerrado.
         */
        canSendMessage() {
            if (!this.selectedRoom) return true
            return ['open'].indexOf(this.selectedRoom.status) !== -1
        },

        /**
         * O atendimento foi encerrado?
         */
        isClosed() {
            if (!this.selectedRoom) return false
            return this.selectedRoom.status == 'closed'
        },

        contextualizedEvents() {
            if (!this.eventos || this.eventos.loading) return []

            return this.eventos.flatMap((evento, index) => {
                let anterior = this.eventos[index - 1]
                let próximo = this.eventos[index + 1]

                let anteriorEnviadoPelaMesmaPessoa =
                    anterior && anterior.autor_type == evento.autor_type && anterior.autor_id == evento.autor_id

                let próximoEnviadoPelaMesmaPessoa =
                    próximo && próximo.autor_type == próximo.autor_type && próximo.autor_id == evento.autor_id

                let enviadoPeloUserAtual = evento.autor_type == 'crm_contacts'

                let eventos = [
                    {
                        ...evento,
                        anteriorEnviadoPelaMesmaPessoa,
                        próximoEnviadoPelaMesmaPessoa,
                        enviadoPeloUserAtual,
                    },
                ]

                let diasDiferentes = anterior && anterior.created_at.substr(0, 10) != evento.created_at.substr(0, 10)
                if (diasDiferentes) {
                    eventos.unshift({ tipo: 'divisão_diária', timestamp: evento.created_at })
                }

                return eventos
            })
        },
    },
    methods: {
        scrollToBottom(force) {
            this.$nextTick(async () => {
                // No chrome em desenvolvimento, o evento para "scroll-to-bottom" é emitido rápido de mais. Tão rápido que o componente
                // vue não conseguiu criar o node no DOM ainda. Pelo o que entendi, isso só acontece quando o usuário visita a página
                // da conversa diretamente no primeiro load.
                if (!this.$refs.mensagens) {
                    await sleep(1000)
                    if (!this.$refs.mensagens) return
                }

                if (force || this.autoScroll) {
                    this.autoScroll = true
                    this.$refs.mensagens.scrollTop = this.$refs.mensagens.scrollHeight
                }
            })
        },

        async loadPreviousEvents() {
            if (!this.selectedRoom) return

            let eventos = this.eventos
            let eventoMaisAntigo = eventos[0]

            await this.$store.dispatch('carregarEventosDaSala', {
                id_cliente: this.selectedRoom.id_cliente,
                id_sala: this.selectedRoom.id,
                token: this.selectedRoom.token,
                referência: eventoMaisAntigo.created_at,
            })

            this.$nextTick(() => {
                let evento = this.$el.querySelector('[data-evento-id="' + eventoMaisAntigo.id + '"]')
                if (evento) evento.scrollIntoView()
            })
        },

        toggleEmojiPicker() {
            this.showingEmojiPicker = !this.showingEmojiPicker
        },

        voltar() {
            this.$store.commit('atualizarEtapa', 'home')
        },

        clearSelectedRoomAndGoToHome() {
            this.$store.commit('deleteChatRoom', this.selectedRoom.id)
            this.$store.commit('atualizarEtapa', 'home')
        },

        onScroll(ev) {
            let diff = ev.target.scrollHeight - ev.target.scrollTop - ev.target.clientHeight

            if (diff < 10) {
                this.autoScroll = true
            } else {
                this.autoScroll = false
            }
        },

        sendCannedFirstMessage(message) {
            this.$store.dispatch('abrirAtendimentoOuEnviarMensagemParaSalaSelecionada', {
                corpo_html: message,
                corpo_texto: message,
            })
        },

        handleSelectOption(option) {
            this.$store.dispatch('abrirAtendimentoOuEnviarMensagemParaSalaSelecionada', {
                mensageiro_id: this.selectedRoom.id,
                mensageiro_type: 'chat_salas',
                visibility: 'public',
                corpo_html: option,
                corpo_texto: option,
                atributos_extras: {},
            })
        },

        startClosing() {
            if (!this.selectedRoom) {
                return
            }

            let eventos = this.eventos
            let peloMenosUmEventoOperador = eventos.find((e) => e.autor_type == 'core_users')

            if (peloMenosUmEventoOperador || this.selectedRoom.status == 'open') {
                this.showClosingDetails = true
            } else {
                this.closeWithRating(null)
            }
        },

        closeWithoutRating() {
            this.closeWithRating(null)
        },

        async closeWithRating(rating) {
            this.isClosing = true

            await this.$store.dispatch('updateChatRoom', {
                room: this.selectedRoom,
                data: {
                    status: 'closed',
                    closing_rating: rating,
                },
            })

            this.isClosing = false

            if (!this.shouldShowRating) {
                this.$store.commit('deleteChatRoom', this.selectedRoom.id)
                this.$store.commit('atualizarEtapa', 'home')
            }

            if (!this.canGoBackToHomeAfterClose) {
                this.$store.commit('atualizarEtapa', 'thank_you_after_close')
            }
        },

        async rateAfterClosing(rating) {
            await this.$store.dispatch('updateChatRoom', {
                room: this.selectedRoom,
                data: { closing_rating: rating },
            })

            if (!this.canGoBackToHomeAfterClose) {
                this.$store.commit('atualizarEtapa', 'thank_you_after_close')
            }
        },
    },
}
</script>


